import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Button } from 'antd';
import { Link } from 'react-router-dom';
import { use100vh } from 'react-div-100vh';

import Header from 'components/Header';
import NavigationTabs from 'components/NavigationTabs';
import styles from './Home.module.less';
import { actions } from 'features/search';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter, FaYoutube } from 'react-icons/fa';
import { AUTH_STATES } from 'constants';
import { ArrowRight } from '@carbon/icons-react';
import { useFlags } from 'launchdarkly-react-client-sdk';

const { Footer } = Layout;

export default function Home() {
  const { showFamilySignupLinks } = useFlags();

  const { authState } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const height = use100vh();

  useEffect(() => {
    dispatch(actions.resetSearch());
  }, [dispatch]);

  return (
    <Layout className={styles.home} style={{ minHeight: height }}>
      <Header
        position="relative"
        className="page-header bg-white border-b border-solid border-gray-300"
        centerContent={
          authState === AUTH_STATES.LOGGED_IN ? (
            <div className="flex flex-col header-center-content">
              <NavigationTabs theme="primary" className="center w-full" />
            </div>
          ) : null
        }
        theme="primary"
      />

      <div
        className="home-content px-5 md:px-12 xl:px-16 flex flex-col xl:flex-row text-center xl:text-left items-center"
        role="main"
      >
        <div>
          <div className="title-container relative z-50">
            <h1 className="leading-tight">
              Child Care Aware
              <span className="text-[0.6em] align-super ml-0.5">®</span>
              <br className="sm:hidden" />
              &nbsp;of America
              <br />
              Fee Assistance Application
            </h1>
          </div>

          <div className="stats-container">
            <p className="pt-6 text-[16px] leading-7 max-w-[500px] m-auto xl:m-0 whitespace-pre-wrap">
              Child Care Aware<span className="text-[0.6em] align-super">®</span> of America is proud to partner with
              the United States Military and Department of Defense to serve military families and support quality child
              care providers through our Fee Assistance and Respite Child Care Programs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-10 justify-center items-center xl:justify-start">
              {showFamilySignupLinks && (
                <Link to="/family/account">
                  <Button
                    type="primary"
                    className="font-semibold !bg-blue-800 hover:!bg-blue-900 text-white transition-all w-64 h-12 !rounded-xl flex items-center justify-center"
                  >
                    Start family application
                    <ArrowRight />
                  </Button>
                </Link>
              )}

              <Link to="/provider/signup">
                <Button className="font-semibold transition-all w-64 h-12 !rounded-xl flex items-center justify-center">
                  Become a provider
                  <ArrowRight />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 xl:mt-0">
          <img
            src="/images/homepage.jpeg"
            alt=""
            className="w-full max-h-[400px] max-w-[600px] rounded-lg rounded-b-none xl:rounded-b-lg shadow-2xl mb-[-2px]"
          />
        </div>
      </div>

      <Footer className="footer w-full z-50 bg-white border-t border-solid border-gray-300 pb-36 xl:pb-7">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center h-full">
          <div className="flex flex-col xl:flex-row justify-start gap-4 xl:gap-6">
            <a
              className="text-[#000] hover:text-primary"
              href="https://www.childcareaware.org/about/site-terms/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Site Terms
            </a>

            <a
              className="text-[#000] hover:text-primary"
              href="https://www.childcareaware.org/about/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>

            <span>Copyright 2025 Child Care Aware® of America</span>
          </div>

          <div className="flex justify-start xl:justify-between space-x-8 xl:space-x-3 h-7 mt-8 xl:mt-0 -ml-1 xl:ml-0">
            <a
              className="text-[#000] text-[28px]"
              href="https://www.facebook.com/ChildCareAware/"
              aria-label="Visit us on Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFacebookF />
            </a>
            <a
              className="text-[#000] text-[28px]"
              href="https://twitter.com/childcareaware"
              aria-label="Visit us on Twitter"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaTwitter />
            </a>
            <a
              className="text-[#000] text-[28px]"
              href="https://www.youtube.com/channel/UCeaTKlEpRfc00TJ537v0RZg"
              aria-label="Visit us on Youtube"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaYoutube />
            </a>
            <a
              className="text-[#000] text-[28px]"
              href="https://www.linkedin.com/company/childcareaware/"
              aria-label="Visit us on Linkedin"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedinIn />
            </a>
            <a
              className="text-[#000] text-[28px]"
              href="https://www.instagram.com/childcareawareusa/"
              aria-label="Visit us on Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram />
            </a>
          </div>
        </div>
      </Footer>
    </Layout>
  );
}
